import React, { useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  PageWrapper,
  HeroWrapper,
  ChartsRow,
  TransactionsRow,
  CashbackRow,
  RecurringRow,
} from "./styles";
import { EmptyState, ErrorState } from "../../../components/States";
import Button, { ButtonTypes } from "../../../components/Button";
import { toSelectOption } from "../../../components/Input";
import { CashbackSection } from "../../../components/CashbackSection";
import { DeletePopUp } from "../../../components/PopUp";
import { AccountPanel } from "../panels/AccountPanel";
import {
  AccountDetailsSkeleton,
  AccountHero,
  BalanceHistorySection,
  CategoryBreakdownSection,
  RecentTransactionsSection,
  RecurringTransactionsSection,
  DeleteAccountModal,
  ActivateAccountModal,
} from "./components";
import {
  useAccountData,
  useAccountActions,
  useAccountPanel,
  useTransactionFilters,
  useTransactionsData,
  useRecurringTransactions,
} from "./hooks";
import { useGetAccountsYearsQuery } from "../../../services/accounts";
import { Transaction } from "../../../services/transactions";
import { DateFormat, formatCurrency, formatDate } from "../../../utils";
import { cashbackAccountTypes } from "../../../utils/cashback";
import {
  getAmountDisplay,
  getPillLabel,
  getPillVariant,
} from "./components/RecentTransactionsSection/utils";
import {
  useTransactionPanel,
  useTransactionsModals,
  useTransactionsActions,
} from "../../Transactions/hooks";
import { TransactionsPanel } from "../../Transactions/panels";
import {
  getTitleFromAction,
  getTransactionObject,
} from "../../Transactions/utils/transformers";
import {
  ACCOUNT_SECTION_TYPES,
  ACCOUNT_TYPE_SECTIONS,
  accountTypeToStringMap,
} from "../types";
import type { AccountDetailsPageProps } from "./types";

const RECENT_TRANSACTIONS_LIMIT = 5;

export const AccountDetailsPage: React.FC<AccountDetailsPageProps> = ({
  accountId,
}) => {
  const [, setLocation] = useLocation();
  const [deleteModal, setDeleteModal] = useState(false);
  const [activateModal, setActivateModal] = useState(false);
  const { data: availableYears = [] } = useGetAccountsYearsQuery(accountId);

  const { account, isLoading, error, notFoundError, refetchAccount } =
    useAccountData(accountId);

  const {
    isDeleting,
    isUpdatingStatus,
    isLoadingImpact,
    deleteError,
    deletionImpact,
    handleDelete,
    handleStatusChange,
    fetchDeletionImpact,
    resetDeleteError,
  } = useAccountActions({
    accountId,
    onDeleteSuccess: () => {
      setDeleteModal(false);
      setLocation("/accounts");
    },
    onStatusChangeSuccess: () => {
      // Tag invalidation from the status mutation refetches the detail query.
      setActivateModal(false);
    },
  });

  const {
    panel: accountPanel,
    openPanel: openAccountPanel,
    closePanel: closeAccountPanel,
    handleSubmit: handleAccountSubmit,
  } = useAccountPanel(accountId);

  const { filters, sortConfig } = useTransactionFilters({
    initialFilters: { rowsPerPage: RECENT_TRANSACTIONS_LIMIT },
  });

  const {
    transactionsData,
    accounts,
    categoriesIncome,
    categoriesExpense,
    isLoading: isLoadingTransactions,
  } = useTransactionsData({
    filters,
    sortConfig,
    accountId,
    activeTab: "transactions",
  });

  const { recurringTransactions, isLoadingRecurring } =
    useRecurringTransactions({
      activeTab: "recurring",
      accountId,
    });

  const {
    panel: txPanel,
    openPanel: openTxPanel,
    closePanel: closeTxPanel,
  } = useTransactionPanel();

  const {
    deleteModal: txDeleteModal,
    selectedTransaction,
    openDeleteModal: openTxDeleteModal,
    closeDeleteModal: closeTxDeleteModal,
  } = useTransactionsModals();

  const {
    handleSubmit: handleTxSubmit,
    handleDeleteConfirm: handleTxDeleteConfirm,
  } = useTransactionsActions({
    accounts,
    panel: txPanel,
    selectedTransaction,
    recurringTransactions,
    closePanel: closeTxPanel,
    closeDeleteModal: closeTxDeleteModal,
  });

  const defaultAccountOption = useMemo(
    () => toSelectOption(accountId, account?.name),
    [accountId, account?.name],
  );

  const handleAddTransaction = () => openTxPanel("create");
  const handleEditTransaction = (tx: Transaction) =>
    openTxPanel("edit", getTransactionObject(tx));
  const handleDuplicateTransaction = (tx: Transaction) =>
    openTxPanel("duplicate", getTransactionObject(tx));
  const handleDeleteTransaction = (tx: Transaction) => openTxDeleteModal(tx);
  const handleViewAllTransactions = () =>
    setLocation(`/transactions?accountId=${accountId}`);
  const handleManageRecurring = () =>
    setLocation(`/transactions?accountId=${accountId}&tab=recurring`);

  if (isLoading) {
    return <AccountDetailsSkeleton />;
  }

  if (notFoundError) {
    return (
      <PageWrapper>
        <EmptyState
          title="Account not found"
          description="The account you're looking for doesn't exist."
          action={
            <Button
              type={ButtonTypes.primary}
              onClick={() => setLocation("/accounts")}
              aria-label="Back to accounts list"
            >
              Back to Accounts
            </Button>
          }
        />
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <ErrorState message={error} onRetry={refetchAccount} />
      </PageWrapper>
    );
  }

  if (!account) {
    return <AccountDetailsSkeleton />;
  }

  // Reachable only by a hand-typed URL: the accounts list is scoped server-side to the types this
  // section owns. Everything below — the balance history, the category breakdown, the transaction
  // list — describes an account whose value is its own transactions. An investment tracked as a
  // holdings ledger keeps its trades in the ledger, not as transactions — the only transactions
  // naming it are the transfers that fund it — so those sections would render an empty or
  // cash-only history that looks like a real one. The
  // server refuses `/balance-history` for the same reason; this is that refusal, said usefully.
  const ownedSection = ACCOUNT_TYPE_SECTIONS[account.accountType];
  if (!ACCOUNT_SECTION_TYPES.includes(account.accountType) && ownedSection) {
    const typeLabel = accountTypeToStringMap[account.accountType];
    const sectionTitle = `${account.name} is managed in ${ownedSection.label}`;
    const sectionDescription = `This is a ${typeLabel} account, so its value and history live in ${ownedSection.label} rather than on an account page.`;
    const goToSection = () => setLocation(ownedSection.path);

    return (
      <PageWrapper>
        <EmptyState
          title={sectionTitle}
          description={sectionDescription}
          action={
            <Button
              type={ButtonTypes.primary}
              onClick={goToSection}
              aria-label={`Go to ${ownedSection.label}`}
            >
              Go to {ownedSection.label}
            </Button>
          }
        />
      </PageWrapper>
    );
  }

  const buildDeleteTransactionDetails = (tx: Transaction) => {
    const pillLabel = getPillLabel(tx.transactionType);
    const { sign, variant } = getAmountDisplay(tx);
    const subtitle = [
      tx.subCategoryName,
      formatDate(tx.transactionDate, DateFormat.DMY_SPACE),
    ]
      .filter(Boolean)
      .join(" · ");

    return {
      pill: { label: pillLabel, variant: getPillVariant(tx.transactionType) },
      title: tx.categoryName ?? pillLabel,
      subtitle,
      amount: `${sign}${formatCurrency(tx.amount)}`,
      amountVariant: variant,
    };
  };

  const openStatusModal = () => {
    setActivateModal(true);
    if (account.isActive) {
      void fetchDeletionImpact();
    }
  };
  const linkedBrokers = deletionImpact?.linkedBrokers ?? [];

  const hasCashbackSection = cashbackAccountTypes.includes(account.accountType);

  const deleteTransactionText = `This will permanently remove this entry from ${account.name}. This action cannot be undone.`;

  const deleteTransactionDetails =
    selectedTransaction && "transactionDate" in selectedTransaction
      ? buildDeleteTransactionDetails(selectedTransaction)
      : undefined;

  return (
    <PageWrapper>
      <HeroWrapper>
        <AccountHero
          account={account}
          isDeleting={isDeleting}
          isUpdatingStatus={isUpdatingStatus}
          onEdit={openAccountPanel}
          onDelete={() => {
            resetDeleteError();
            setDeleteModal(true);
            void fetchDeletionImpact();
          }}
          onStatusChange={openStatusModal}
        />
      </HeroWrapper>

      <ChartsRow>
        <BalanceHistorySection
          accountId={accountId}
          availableYears={availableYears}
        />
        <CategoryBreakdownSection
          accountId={accountId}
          availableYears={availableYears}
        />
      </ChartsRow>

      <TransactionsRow>
        <RecentTransactionsSection
          transactions={transactionsData.transactions}
          totalRows={transactionsData.pagination.totalRows}
          isLoading={isLoadingTransactions}
          onAdd={handleAddTransaction}
          onEdit={handleEditTransaction}
          onDuplicate={handleDuplicateTransaction}
          onDelete={handleDeleteTransaction}
          onViewAll={handleViewAllTransactions}
        />
      </TransactionsRow>

      <CashbackRow>
        {hasCashbackSection && (
          <CashbackSection
            accountId={accountId}
            cashbackMode={account.cashbackMode}
          />
        )}
      </CashbackRow>

      <RecurringRow>
        <RecurringTransactionsSection
          recurringTransactions={recurringTransactions}
          isLoading={isLoadingRecurring}
          onManage={handleManageRecurring}
        />
      </RecurringRow>

      {accountPanel.isOpen && (
        <AccountPanel
          account={accountPanel.account}
          handleOnSubmit={handleAccountSubmit}
          isOpen={accountPanel.isOpen}
          onClose={closeAccountPanel}
        />
      )}

      {txPanel.isOpen && (
        <TransactionsPanel
          title={getTitleFromAction(txPanel.action)}
          action={txPanel.action}
          isOpen={txPanel.isOpen}
          incomeCategoryList={categoriesIncome}
          expenseCategoryList={categoriesExpense}
          onClose={closeTxPanel}
          transaction={txPanel.transaction}
          handleOnSubmit={handleTxSubmit}
          defaultAccount={defaultAccountOption}
        />
      )}

      {txDeleteModal && selectedTransaction && (
        <DeletePopUp
          isOpen={txDeleteModal}
          setIsOpen={closeTxDeleteModal}
          title="Delete this transaction?"
          text={deleteTransactionText}
          details={deleteTransactionDetails}
          confirmLabel="Delete transaction"
          handleOnDelete={handleTxDeleteConfirm}
        />
      )}

      <DeleteAccountModal
        isOpen={deleteModal}
        accountName={account.name}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        deletionImpact={deletionImpact}
        isLoadingImpact={isLoadingImpact}
        isDeleting={isDeleting}
        deleteError={deleteError}
      />

      <ActivateAccountModal
        isOpen={activateModal}
        account={account}
        onClose={() => setActivateModal(false)}
        onConfirm={() => handleStatusChange(account.isActive)}
        linkedBrokers={linkedBrokers}
        isCheckingLinkedBrokers={isLoadingImpact}
      />
    </PageWrapper>
  );
};
